import React, { FC, FormEvent, useEffect } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { greys, mainColors } from '../../../../../styling/theme';
import { ClassNameMap } from '@mui/styles';
import {
  UserAuthPreferencesData,
  getAuthMessageColor,
} from '../SecuritySettings.component';
import TokenService from '../../../../../services/token-service';
import FastAPIInstance from '../../../../../services/risksystem3-public-api';

const useStyles = makeStyles(() => ({
  settingsContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'white',
    padding: '0.62rem',
    borderRadius: '0.25rem',
  },
  settingsTitle: {
    fontSize: '1.25rem',
    borderBottom: `1px solid ${mainColors.mainBlue}`,
    width: 'fit-content',
    color: mainColors.mainBlue,
    fontWeight: 600,
    marginBottom: '0.62rem',
  },
  settingsSection: {
    display: 'flex',
    flexDirection: 'row',
    gap: '0.62rem',
    padding: '0.62rem',
  },
  settingsItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.62rem',
  },
  settingsItemTitle: {
    fontSize: '0.94rem',
    borderBottom: `1px solid ${mainColors.mainBlue}`,
    width: 'fit-content',
    color: mainColors.mainBlue,
    fontWeight: 600,
  },
  settingsItemDescription: {
    fontSize: '0.75rem',
    color: mainColors.mainBlue,
    fontWeight: 400,
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.62rem',
  },
  settingsItemValueContainer: {
    display: 'flex',
    gap: '0.62rem',
    color: mainColors.mainBlue,
    fontWeight: 600,
    alignItems: 'center',
    fontSize: '1.00rem',
  },
  settingsItemValue: {
    all: 'unset',
    fontSize: '1.00rem',
    fontWeight: 400,
    padding: '0.31rem 0.62rem',
    color: mainColors.mainBlue,
    border: `1px solid ${mainColors.mainBlue}`,
    width: '6.25rem',
  },
  submitChangeContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '0.62rem',
    alignItems: 'center',
    justifyContent: 'flex-start',
    height: '1.88rem',
  },
  submitEditButton: {
    all: 'unset',
    width: 'fit-content',
    display: 'flex',
    flexDirection: 'row',
    gap: '0.62rem',
    padding: '0.31rem 0.62rem',
    borderRadius: '0.25rem',
    fontWeight: 400,
    fontSize: '0.94rem',
    backgroundColor: mainColors.mainBlue,
    color: 'white',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: mainColors.mainBlue_lighter,
    },
    '&:active': {
      backgroundColor: mainColors.mainBlue_slightlyDarker,
    },
  },
  submitEditButton_inactive: {
    all: 'unset',
    width: 'fit-content',
    display: 'flex',
    flexDirection: 'row',
    gap: '0.62rem',
    padding: '0.31rem 0.62rem',
    borderRadius: '0.25rem',
    fontWeight: 400,
    fontSize: '0.94rem',
    backgroundColor: greys.grey300,
    color: 'white',
    userSelect: 'none',
  },
}));

// this is where the data will be passed in from the parent component
// be sure to also update the "No settings to display" message to include any changes to these props
interface Props {
  password_update_frequency_days: UserAuthPreferencesData | undefined;
  reloadSettings: () => void;
}

const PasswordSettings: FC<Props> = ({
  password_update_frequency_days,
  reloadSettings,
}) => {
  const classes: ClassNameMap<string> = useStyles();

  const [resetFrequency, setResetFrequency] = React.useState<number | null>(
    password_update_frequency_days
      ? (password_update_frequency_days.attribute_value as unknown as number)
      : 90,
  );

  useEffect(() => {
    if (
      password_update_frequency_days &&
      (password_update_frequency_days.attribute_value as unknown as number) !==
        resetFrequency
    ) {
      setResetFrequency(
        password_update_frequency_days.attribute_value as unknown as number,
      );
    }
  }, [password_update_frequency_days]);

  const [resetFrequencyRequestStatus, setResetFrequencyRequestStatus] =
    React.useState<string>('idle');
  const [resetFrequencyRequestMessage, setResetFrequencyRequestMessage] =
    React.useState<string>('');

  const handleChangeResetFrequency = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const validatedFrequency =
      resetFrequency === null || resetFrequency < 1 ? 1 : resetFrequency;
    setResetFrequency(validatedFrequency);

    setResetFrequencyRequestStatus('loading');
    setResetFrequencyRequestMessage('Updating password reset frequency...');
    FastAPIInstance.put('/v1/settings/auth_pref_attrs', {
      client_id: TokenService.getUser().user_client_id,
      attribute_name: 'password_update_frequency_days',
      attributes: {
        attribute_value: validatedFrequency,
      },
    })
      .then((response) => {
        if (response.status === 200) {
          setResetFrequencyRequestStatus('success');
          setResetFrequencyRequestMessage('Updated successfully.');
          reloadSettings();
        } else {
          setResetFrequencyRequestStatus('fail');
          setResetFrequencyRequestMessage(response.data.detail);
        }
      })
      .catch((error) => {
        setResetFrequencyRequestStatus('fail');
        setResetFrequencyRequestMessage(
          error.response?.data?.detail ||
            'An error occurred while updating the password reset frequency',
        );
      })
      .finally(() => {
        setTimeout(() => {
          setResetFrequencyRequestStatus('idle');
          setResetFrequencyRequestMessage('');
        }, 5000);
      });
  };

  return (
    <div className={classes.settingsContainer}>
      <div className={classes.settingsTitle}>Password Settings</div>
      {!password_update_frequency_days ? (
        <div className={classes.settingsSection}>
          <div className={classes.settingsItem}>
            <div className={classes.settingsItemDescription}>
              No settings to display
            </div>
          </div>
        </div>
      ) : null}
      {password_update_frequency_days ? (
        <div className={classes.settingsSection}>
          <div className={classes.settingsItem}>
            <div className={classes.settingsItemTitle}>
              Password Update Frequency
            </div>
            <div className={classes.settingsItemDescription}>
              This is the frequency at which users will be suggested to reset
              their passwords. Security best practices recommend that passwords
              be reset at least every 90 days.
            </div>
            <form
              className={classes.formContainer}
              onSubmit={(e: FormEvent<HTMLFormElement>) =>
                handleChangeResetFrequency(e)
              }
            >
              <div className={classes.settingsItemValueContainer}>
                <input
                  type="number"
                  min={1}
                  max={365}
                  step={1}
                  value={resetFrequency ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value;
                    setResetFrequency(value === '' ? null : Number(value));
                  }}
                  className={classes.settingsItemValue}
                />
                Days
              </div>
              <div className={classes.submitChangeContainer}>
                {(password_update_frequency_days.attribute_value as unknown as number) !==
                resetFrequency ? (
                  <button type="submit" className={classes.submitEditButton}>
                    Submit Change
                  </button>
                ) : (
                  <div className={classes.submitEditButton_inactive}>
                    Submit Change
                  </div>
                )}
                {resetFrequencyRequestStatus != 'idle' ? (
                  <div
                    style={{
                      color: getAuthMessageColor(resetFrequencyRequestStatus),
                    }}
                  >
                    <h3>{resetFrequencyRequestMessage}</h3>
                  </div>
                ) : null}
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PasswordSettings;
